import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { client } from '../lib/sanity'
import PresentationViewer from '../components/PresentationViewer'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import Seo from '../components/Seo'

const slugify = (str = '') => str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')

const getReadingTime = (text = '') => {
    const words = text.trim().split(/\s+/).filter(Boolean).length
    const minutes = Math.max(1, Math.round(words / 200))
    return { words, minutes }
}

const plainTextExcerpt = (markdown = '', len = 160) => {
    const text = markdown
        .replace(/[#>*_`~[\]()!-]/g, ' ')
        .replace(/https?:\/\/\S+/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    return text.length > len ? text.slice(0, len).trim() + '…' : text
}

export default function BlogPost() {
    const { slug } = useParams()
    const [post, setPost] = useState(null)
    const [loading, setLoading] = useState(true)
    const [progress, setProgress] = useState(0)
    const [related, setRelated] = useState([])
    const articleRef = useRef(null)
    // Removed theme state
    const [activeId, setActiveId] = useState('')

    // Removed theme effect

    useEffect(() => {
        setLoading(true)
        client
            .fetch(`*[_type == "blog" && slug.current == $slug][0]{
            _id,
            _createdAt,
            title,
            slug,
            author,
            "image": mainImage.asset->url,
            category->{title, "slug": slug.current},
            subcategory->{title, "slug": slug.current},
            content,
            markdownContent,
            isPresentation,
            "presentationPdfUrl": presentationPdf.asset->url
          }`, { slug })
            .then(async (data) => {
                setPost(data)
                setLoading(false)
                if (data?.category?.slug || data?.subcategory?.slug) {
                    const rel = await client.fetch(`*[_type=="blog" && slug.current != $slug && (
                category->slug.current == $cat || subcategory->slug.current == $sub
              )][0...3]{
                title,
                slug,
                "image": mainImage.asset->url,
                category->{title},
                subcategory->{title}
              }`, { slug, cat: data?.category?.slug, sub: data?.subcategory?.slug })
                    setRelated(rel || [])
                } else {
                    setRelated([])
                }
            })
    }, [slug])

    useEffect(() => {
        const onScroll = () => {
            const el = articleRef.current
            if (!el) return
            const total = el.scrollHeight - window.innerHeight
            const scrolled = Math.min(total, Math.max(0, window.scrollY - (el.offsetTop || 0)))
            const pct = total > 0 ? Math.round((scrolled / total) * 100) : 0
            setProgress(pct)
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!post) return
        const rootEl = articleRef.current
        if (!rootEl) return

        const headingEls = Array.from(rootEl.querySelectorAll('h2[id], h3[id]'))
        if (!headingEls.length) return

        const OFFSET = 120 // aligns with scroll-mt-24
        const getOffsets = () => headingEls.map(el => ({ id: el.id, top: el.getBoundingClientRect().top + window.scrollY }))

        let offsets = getOffsets()

        const onScroll = () => {
            const y = window.scrollY + OFFSET
            let current = offsets[0].id
            for (const o of offsets) {
                if (o.top <= y) current = o.id
                else break
            }
            setActiveId(current)
        }

        const onResize = () => {
            offsets = getOffsets()
            onScroll()
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onResize)

        // Recalculate when images inside the article load (content height changes)
        const imgs = rootEl.querySelectorAll('img')
        imgs.forEach(img => img.addEventListener('load', onResize))

        onResize()

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onResize)
            imgs.forEach(img => img.removeEventListener('load', onResize))
        }
    }, [post])

    const { minutes } = useMemo(() => getReadingTime(post?.markdownContent || ''), [post])

    const toc = useMemo(() => {
        const md = post?.markdownContent || ''
        const lines = md.split('\n')
        const items = []
        lines.forEach(line => {
            const m2 = line.match(/^##\s+(.+)/)
            const m3 = line.match(/^###\s+(.+)/)
            if (m2) {
                const text = m2[1].trim()
                items.push({ depth: 2, text, id: slugify(text) })
            } else if (m3) {
                const text = m3[1].trim()
                items.push({ depth: 3, text, id: slugify(text) })
            }
        })
        return items
    }, [post])

    const mdComponents = {
        h2: ({ children }) => {
            const text = String(children)
            const id = slugify(text)
            return <h2 id={id} className="scroll-mt-24">{children}</h2>
        },
        h3: ({ children }) => {
            const text = String(children)
            const id = slugify(text)
            return <h3 id={id} className="scroll-mt-24">{children}</h3>
        },
        img: ({ src = '', alt = '' }) => (
            <img src={src} alt={alt} loading="lazy" className="border-2 border-black dark:border-white" />
        ),
        a: ({ href = '', children }) => (
            <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noopener noreferrer' : undefined} className="text-accent underline decoration-2 hover:no-underline">
                {children}
            </a>
        ),
        table: ({ children }) => (
            <table className="min-w-full border-collapse">
                {children}
            </table>
        ),
        thead: ({ children }) => (
            <thead className="bg-zinc-50 dark:bg-zinc-800/50">
                {children}
            </thead>
        ),
        tbody: ({ children }) => (
            <tbody>
                {children}
            </tbody>
        ),
        tr: ({ children }) => (
            <tr className="border-b border-zinc-200 dark:border-zinc-800">
                {children}
            </tr>
        ),
        th: ({ children }) => (
            <th className="text-left font-semibold px-3 py-2 border border-zinc-200 dark:border-zinc-700">
                {children}
            </th>
        ),
        td: ({ children }) => (
            <td className="align-top px-3 py-2 border border-zinc-200 dark:border-zinc-700">
                {children}
            </td>
        ),
        pre: ({ children }) => (
            <pre className="overflow-auto border-2 border-black dark:border-white bg-zinc-900 text-zinc-100 p-3">
                {children}
            </pre>
        ),
        code: ({ inline, children }) => (
            inline
                ? <code className="px-1 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100">{children}</code>
                : <code>{children}</code>
        ),
    }

    if (loading) return <div className="p-6 font-mono">Loading post.</div>

    if (!post) {
        return (
            <div className="min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center p-6">
                <Seo title="Post not found — Dristanta Silwal" noindex canonicalPath={`/blog/${slug}`} />
                <div className="text-center max-w-md">
                    <h1 className="text-2xl font-serif font-bold text-black dark:text-white mb-2">Post not found</h1>
                    <p className="text-zinc-600 dark:text-zinc-400 mb-6">
                        This post may have been moved or no longer exists.
                    </p>
                    <Button as={Link} to="/blog">← Back to Blog</Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <Seo
                title={`${post.title} — Dristanta Silwal`}
                description={plainTextExcerpt(post.markdownContent)}
                canonicalPath={`/blog/${post.slug?.current || slug}`}
                image={post.image}
                type="article"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'BlogPosting',
                    headline: post.title,
                    image: post.image ? [post.image] : undefined,
                    datePublished: post._createdAt,
                    author: { '@type': 'Person', name: post.author || 'Dristanta Silwal' },
                    mainEntityOfPage: `https://dristantasilwal.com.np/blog/${post.slug?.current || slug}`,
                }}
            />

                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="mb-6 flex items-center justify-between">
                    <Button as={Link} to="/blog" variant="secondary">
                        <span className="text-lg">←</span>
                        <span>Back to Blog</span>
                    </Button>
                </div>

                    {post.image && !post.isPresentation && (
                        <img src={post.image} alt={post.title} loading="lazy" className="border-2 border-black dark:border-white w-full mb-6" />
                    )}

                    <h1 className="text-4xl font-serif font-bold tracking-tight text-black dark:text-white mb-2">{post.title}</h1>
                    <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-8 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {post.isPresentation && (
                            <>
                                <Badge tone="accent">Presentation</Badge>
                                <span>•</span>
                            </>
                        )}
                        <span>{post.category?.title}{post.subcategory?.title ? ` / ${post.subcategory.title}` : ''}</span>
                        <span>•</span>
                        <time dateTime={post._createdAt}>{new Date(post._createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</time>
                        {!post.isPresentation && (
                            <>
                                <span>•</span>
                                <span>{minutes} min read</span>
                            </>
                        )}
                    </div>


                    <div className={`grid ${post.isPresentation ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-[1fr_280px]'} gap-10`} ref={articleRef}>
                        {/* Article */}
                        <article className="space-y-6 min-w-0">
                            {/* PDF Viewer for Presentations */}
                            {post.isPresentation && post.presentationPdfUrl && (
                                <PresentationViewer pdfUrl={post.presentationPdfUrl} title={post.title} />
                            )}

                            {!post.isPresentation && post.markdownContent && (
                                <div className="prose lg:prose-lg max-w-none dark:prose-invert prose-headings:font-serif prose-headings:text-black dark:prose-headings:text-white prose-p:text-zinc-800 dark:prose-p:text-zinc-300 prose-strong:text-black dark:prose-strong:text-white prose-em:text-zinc-800 dark:prose-em:text-zinc-300 prose-a:text-accent prose-a:underline prose-li:text-zinc-800 dark:prose-li:text-zinc-300 overflow-visible min-w-0">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        rehypePlugins={[rehypeRaw]}
                                        components={mdComponents}
                                    >
                                        {post.markdownContent}
                                    </ReactMarkdown>
                                    <style>{`
                                        .prose table {
                                            display: table;
                                            width: 100%;
                                            border-collapse: collapse;
                                            table-layout: auto;
                                            overflow: visible;
                                        }
                                        .prose th, .prose td {
                                            border: 1px solid var(--tw-prose-borders, #e5e7eb);
                                            padding: .5rem .75rem;
                                            white-space: normal;
                                            word-break: break-word;
                                            overflow-wrap: anywhere;
                                            vertical-align: top;
                                        }
                                        .prose th {
                                            background: #f9fafb;
                                            text-align: left;
                                        }
                                        @media (prefers-color-scheme: dark) {
                                          .dark .prose th { background: rgba(255,255,255,.05); }
                                          .dark .prose th, .dark .prose td { border-color: #374151; }
                                        }
                                    `}</style>
                                </div>
                            )}

                            {/* Share */}
                            <div className="pt-6 mt-6 border-t-2 border-black dark:border-white">
                                <div className="flex flex-wrap items-center gap-3 text-sm">
                                    <span className="text-zinc-700 font-medium dark:text-zinc-300">Share:</span>
                                    <Button as="a" variant="secondary" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer">Twitter</Button>
                                    <Button as="a" variant="secondary" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer">LinkedIn</Button>
                                    <Button variant="secondary" onClick={() => navigator.clipboard.writeText(window.location.href)}>Copy link</Button>
                                </div>
                            </div>
                        </article>

                        {/* TOC - Hidden for presentations */}
                        {!post.isPresentation && (
                            <aside className="hidden md:block">
                                <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-auto overscroll-contain border-2 border-black dark:border-white p-4 bg-white dark:bg-zinc-950">
                                    <div className="text-xs font-mono font-semibold text-zinc-600 mb-2 uppercase tracking-wide dark:text-zinc-400">On this page</div>
                                    <nav className="space-y-1">
                                        {toc.length === 0 && <div className="text-sm text-zinc-500 dark:text-zinc-400">No sections</div>}
                                        {toc.map((t, i) => (
                                            <a
                                                key={i}
                                                href={`#${t.id}`}
                                                onClick={(e) => {
                                                    // smooth scroll to avoid instant jump
                                                    e.preventDefault()
                                                    const el = document.getElementById(t.id)
                                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                                    window.history.replaceState(null, '', `#${t.id}`)
                                                }}
                                                className={`block text-sm ${t.depth === 2 ? 'pl-2' : 'pl-6'} transition-colors border-l-2 ${activeId === t.id ? 'border-accent text-accent' : 'border-transparent text-zinc-700 dark:text-zinc-300 hover:text-accent'}`}
                                            >
                                                {t.text}
                                            </a>
                                        ))}
                                    </nav>
                                </div>
                            </aside>
                        )}
                    </div>

                    {/* Related posts */}
                    {related.length > 0 && (
                        <div className="mt-12 pt-8 border-t-2 border-black dark:border-white">
                            <h2 className="text-xl font-serif font-bold text-black dark:text-white mb-4">Related posts</h2>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {related.map((r) => (
                                    <Link key={r.slug?.current || r.slug} to={`/blog/${r.slug?.current || r.slug}`} className="group border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4 shadow-brutal-sm hover:shadow-brutal transition-shadow">
                                        {r.image && <img src={r.image} alt={r.title} loading="lazy" className="mb-3 w-full h-40 object-cover border-2 border-black dark:border-white" />}
                                        <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">{r.category?.title}{r.subcategory?.title ? ` / ${r.subcategory.title}` : ''}</div>
                                        <div className="font-semibold text-black dark:text-white group-hover:text-accent">{r.title}</div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Back to top button */}
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="fixed bottom-6 right-6 z-50"
                        aria-label="Go to top"
                        title="Back to top"
                    >
                        <div
                            className="relative h-12 w-12 rounded-full shadow-brutal"
                            style={{
                                background: `conic-gradient(#FF5F1F ${progress}%, #e5e7eb ${progress}%)`
                            }}
                        >
                            <div className="absolute inset-1 rounded-full bg-black text-white flex items-center justify-center hover:bg-accent hover:text-black transition-colors">
                                ↑
                            </div>
                        </div>
                    </button>
                </div>
            </div>
    )
}